import React, { useEffect, useState } from 'react';
import { Form, Badge, Card, CardBody, CardFooter } from 'reactstrap';
import { sortBy, chunk, compact, flatten, slice } from 'lodash';
import { Field, CardHeader, PageTitle, Button } from '@cordata/buffet';
import styled from 'styled-components';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import { Link, useParams, useHistory } from 'react-router-dom';

import { authorizedInstance as axios } from '~/utils';

const SCardHeader = styled(CardHeader)`
  background-color: ${p => p.bg};
`;

const SBadge = styled(Badge)`
  white-space: normal;
  text-align: left;
  line-height: 1.5;
  background-color: ${p => p.bg};
`;

const ColWidth = styled.div`
  flex: 1;
`;

const Option = ({ option: b }) => (
  <SBadge
    color={!b.attributes.color ? 'tertiary' : ''}
    className="text-light"
    bg={b.attributes.color}
  >
    {b.attributes.badgeLabel}
  </SBadge>
);

Option.propTypes = {
  option: PropTypes.shape().isRequired,
};

const SField = styled.div`
  &,
  .form-group {
    margin-bottom: 0;
  }

  .form-check {
    padding: .5rem;
    padding-left: 1.5rem;
  }

  .form-control {
    border: 0;
  }
`;

const PatientBadges = () => {
  const history = useHistory();
  const params = useParams();
  const [badgeGroups, setBadgeGroups] = useState([]);
  const [patientBadges, setPatientBadges] = useState([]);
  const [chunked, setChunked] = useState([]);

  useEffect(() => {
    axios
      .get('/reference-data/reference-badge-groups?include=["reference-badges"]')
      .then(r => r.data.data)
      .then(r => sortBy(r, ['attributes.defaultSortOrder', 'attributes.badgeGroupLabel']))
      .then(setBadgeGroups)
      .catch(console.error);

    axios
      .get(`/patients/${params.patient_id}/patient-badges`)
      .then(r => r.data.data)
      .then(setPatientBadges)
      .catch(console.error);
  }, []);

  useEffect(() => {
    let c = 3; // no. of columns

    if (window.innerWidth < 576) {
      c = 1;
    } else if (window.innerWidth < 768) {
      c = 2;
    }

    const l = Math.ceil(badgeGroups.length / c);

    setChunked(chunk(badgeGroups, l));
  }, [badgeGroups]);

  return (
  <>
    <PageTitle>Badges</PageTitle>

    <Formik
      displayName="patient-badges-formik"
      enableReinitialize

      initialValues={{
      ...badgeGroups.reduce((a, m) => ({
          ...a,
          [m.attributes.badgeGroupName]: m.attributes.isExclusive
            ? m.relationships.referenceBadges.filter(b =>
                patientBadges
                  .map(pb => pb.relationships.referenceBadge.id)
                  .includes(b.id))
                  .map(b => b.id)[0]
            : m.relationships.referenceBadges.filter(b =>
                patientBadges
                  .map(pb => pb.relationships.referenceBadge.id)
                  .includes(b.id)),
        }), {}),
      }}

      onSubmit={(values, { setSubmitting, setErrors }) => {
        const proposedBadges = compact(flatten(Object.values(values)));

        const fetchId = b => ((typeof b === 'string')
          ? b
          : b.id
        );

        const creates = flatten(proposedBadges.filter(b =>
          !patientBadges
            .map(pb => pb.relationships.referenceBadge.id)
            .includes(b))).map(b => ({
              badge_uid: slice(fetchId(b).split('-'), 0, 5).join('-'),
              badge_group_uid: slice(fetchId(b).split('-'), 5).join('-'),
            }));
        const deletes = patientBadges.filter(b =>
          !proposedBadges.includes(b.relationships.referenceBadge.id)).map(b =>
            ({ patient_badge_uid: b.id }));

        const data = [
          `${encodeURIComponent('create')}=${encodeURIComponent(JSON.stringify(creates))}`,
          `${encodeURIComponent('delete')}=${encodeURIComponent(JSON.stringify(deletes))}`,
        ].join('&').replace(/%20/g, '+');

        axios
          .put(`/patients/${params.patient_id}/patient-badges`, data, {
            headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
          })
        .then(r => r.data.data)
          .then(() => history.push(`/patients/${params.patient_id}`, {
            refresh: {
              badges: Math.random(),
          },
          }))
        .catch(setErrors)
        .then(() => setSubmitting(false));
      }}
    >
      {({
        handleSubmit,
        isSubmitting,
      }) => (
        <Form onSubmit={handleSubmit} noValidate>
          <Card>
            <CardBody>
              <div className="d-flex flex-row w-100">
                {chunked.map((c, i) => (
                  <ColWidth
                    key={c.map(bG => bG.id)}
                    className={i !== chunked.length - 1 && 'pr-3'}
                  >
                    {c.map(bG => (
                      <Card className="mb-3">
                        <SCardHeader
                          color={!bG.attributes.badgeGroupColor ? 'tertiary' : ''}
                          inverse
                          bg={bG.attributes.badgeGroupColor}
                        >
                          {bG.attributes.badgeGroupLabel}
                        </SCardHeader>

                        <SField>
                          <Field
                            name={bG.attributes.badgeGroupName}
                            label={false}
                            type={bG.attributes.isExclusive ? 'radio-group' : 'checkbox-group'}
                            inline={false}
                            options={sortBy(bG.relationships.referenceBadges, ['attributes.defaultSortOrder', 'attributes.badgeLabel'])
                            }
                            components={{ Option }}
                            getOptionValue={o => o.id}
                            getOptionLabel={o => o.badgeName}
                          />
                        </SField>
                      </Card>
                    ))}
                  </ColWidth>
                ))}
              </div>
            </CardBody>

            <CardFooter className="d-flex">
              <div className="ml-auto">
                <Button type="cancel" disabled={isSubmitting} tag={Link} to={`/patients/${params.patient_id}/details`}>
                  Cancel
                </Button>

                <Button type="submit" disabled={isSubmitting}>
                  Save
                </Button>
              </div>
            </CardFooter>
          </Card>
        </Form>
      )}
    </Formik>
  </>
  );
};

export default PatientBadges;
